import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavigationType, useNavigationType } from 'react-router-dom';

import { IconEye } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { fetchDialysisAdmissions } from './dialysis.api';
import { LookupSelect } from './dialysis.shared';
import { useDialysisAdmissions, useDialysisLookup } from './dialysis.queries';
import { formatDialysisDate, formatDialysisDateTime, formatDialysisTime, todayInput } from './dialysis.utils';
import type { DialysisAdmission, DialysisListFilters } from './types';

const initialFilters = (): DialysisListFilters => ({ page: 1, limit: 20, fromDate: todayInput(), toDate: todayInput() });

const FILTERS_STORAGE_KEY = 'dialysis-list-filters';

const readStoredFilters = (): DialysisListFilters => {
  const fallback = initialFilters();
  try {
    const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<DialysisListFilters> | null;
    if (!parsed || typeof parsed !== 'object') return fallback;
    return {
      page: typeof parsed.page === 'number' && parsed.page > 0 ? parsed.page : fallback.page,
      limit: typeof parsed.limit === 'number' && parsed.limit > 0 ? parsed.limit : fallback.limit,
      ...(parsed.field ? { field: parsed.field } : {}),
      ...(typeof parsed.value === 'string' ? { value: parsed.value } : {}),
      ...(typeof parsed.fromDate === 'string' ? { fromDate: parsed.fromDate } : {}),
      ...(typeof parsed.toDate === 'string' ? { toDate: parsed.toDate } : {}),
      ...(parsed.sortBy ? { sortBy: parsed.sortBy, sortOrder: parsed.sortOrder === 'desc' ? 'desc' as const : 'asc' as const } : {}),
    };
  } catch {
    return fallback;
  }
};

export const DialysisListPage = () => {
  const returning = useNavigationType() === NavigationType.Pop;
  const [storedFilters] = useState(() => (returning ? readStoredFilters() : initialFilters()));
  const [draft, setDraft] = useState<DialysisListFilters>(storedFilters);
  const [filters, setFilters] = useState<DialysisListFilters>(storedFilters);
  const doctors = useDialysisLookup('doctors');
  const machines = useDialysisLookup('dialysis-machines');
  const query = useDialysisAdmissions(filters);
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });
  const busy = query.isFetching;

  useEffect(() => {
    window.sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    const totalPages = query.data?.pagination.totalPages;
    if (totalPages && filters.page > totalPages) setFilters((current) => ({ ...current, page: totalPages }));
  }, [filters.page, query.data?.pagination.totalPages]);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    setFilters({ ...draft, page: 1 });
  };
  const reset = () => { const next = initialFilters(); setDraft(next); setFilters(next); };
  const today = () => { const date = todayInput(); setDraft((current) => ({ ...current, fromDate: date, toDate: date })); setFilters((current) => ({ ...current, fromDate: date, toDate: date, page: 1 })); };

  const exportToExcel = async () => {
    const rows = await fetchAllPages<DialysisAdmission>((page, limit) => fetchDialysisAdmissions({ ...filters, page, limit }));
    await exportRowsToExcel({
      fileName: `Dialysis-Admissions-${todayInput()}`,
      title: 'Dialysis Admissions',
      subtitle: filters.fromDate && filters.toDate ? `${formatDialysisDate(filters.fromDate)} to ${formatDialysisDate(filters.toDate)}` : undefined,
      columns: [
        { header: 'SN', width: 6, align: 'right', map: (_row, index) => index + 1 },
        { header: 'Dialysis No', width: 12, align: 'right', map: (row) => row.dialysisNumber },
        { header: 'Patient Name', width: 24, map: (row) => row.patientSnapshot.name },
        { header: 'UHID', width: 16, map: (row) => row.patientSnapshot.uhid ?? '-' },
        { header: 'Gender', width: 10, map: (row) => row.patientSnapshot.gender },
        { header: 'Age', width: 12, map: (row) => `${row.patientSnapshot.ageYears}Y ${row.patientSnapshot.ageMonths}M ${row.patientSnapshot.ageDays}D` },
        { header: 'Phone', width: 14, map: (row) => row.patientSnapshot.phone ?? '-' },
        { header: 'Visit Type', width: 11, map: (row) => row.visitType },
        { header: 'Under Doctor', width: 22, map: (row) => doctorNameOrDash(row.underDoctor?.name) },
        { header: 'Department', width: 18, map: (row) => row.underDoctor?.department ?? '-' },
        { header: 'Admission Date', width: 20, map: (row) => formatDialysisDateTime(row.admissionDate) },
        { header: 'Machine', width: 16, map: (row) => row.dialysisMachine.name },
        { header: 'Session Time', width: 16, map: (row) => `${formatDialysisTime(row.startTime)} - ${formatDialysisTime(row.endTime)}` },
        { header: 'Patient Type', width: 16, map: (row) => row.insuranceType },
        { header: 'Bed Category', width: 16, map: (row) => row.bedCategory.name },
        { header: 'Bed', width: 10, map: (row) => row.bed.name },
        { header: 'Status', width: 12, map: (row) => row.status },
      ],
      rows,
    });
  };

  return (
    <div className="page-stack ipd-daycare-page">
      <div className="page-heading"><div><p className="eyebrow">Dialysis</p><h1>Dialysis List</h1><p>Sessions, bed assignments, and visit details.</p></div><div className="page-heading-actions"><ExcelExportButton onExport={exportToExcel} disabled={!query.isSuccess} /><Link className="button button-primary" to="/dialysis/register">New Dialysis Register</Link></div></div>
      <section className="content-card ipd-daycare-card">
        <form className="ipd-daycare-filter-grid dialysis-filter-grid" onSubmit={apply}>
          <select value={draft.field ?? ''} onChange={(event) => setDraft((current) => ({ ...current, field: event.target.value as DialysisListFilters['field'], value: '' }))}>
            <option value="">SELECT FIELD</option><option value="patientName">PATIENT NAME</option><option value="uhid">UHID</option><option value="phone">Phone No</option><option value="dialysisNo">Dialysis No</option><option value="underDoctor">Under Doctor</option><option value="machine">Machine</option>
          </select>
          {draft.field === 'underDoctor' ? <LookupSelect id="dialysis-doctor-filter" value={draft.value ?? ''} options={doctors.data} placeholder="Select Doctor" onChange={(value) => setDraft((current) => ({ ...current, value }))} /> : draft.field === 'machine' ? <LookupSelect id="dialysis-machine-filter" value={draft.value ?? ''} options={machines.data} placeholder="Select Machine" onChange={(value) => setDraft((current) => ({ ...current, value }))} /> : <input value={draft.value ?? ''} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} placeholder="Field Value" disabled={!draft.field} />}
          <input type="date" value={draft.fromDate ?? ''} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} aria-label="From date" />
          <input type="date" value={draft.toDate ?? ''} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} aria-label="To date" />
          <div className="ipd-daycare-filter-actions"><button className="button button-primary" type="submit" disabled={busy}>Filter</button><button className="button ipd-daycare-today" type="button" onClick={today}>Today</button><button className="button button-secondary" type="button" onClick={reset}>Reset</button></div>
        </form>
        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load dialysis admissions" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && query.data.data.length === 0 ? <EmptyState title="No dialysis admissions found" message="Change the filters or create a new admission." /> : null}
        {query.isSuccess && query.data.data.length > 0 ? <>
          <div className="table-scroll"><table className="ipd-daycare-table"><thead><tr><th>SN</th><SortableTh control={tableSort} column="dialysisNo">Dialysis No</SortableTh><SortableTh control={tableSort} column="patient">Patient Details</SortableTh><SortableTh control={tableSort} column="doctor">Under Doctor</SortableTh><SortableTh control={tableSort} column="admissionDate">Details</SortableTh><SortableTh control={tableSort} column="bed">Bed Details</SortableTh><th>Action</th></tr></thead><tbody>{query.data.data.map((row, index) => <tr key={row.id}>
            <td>{(filters.page - 1) * filters.limit + index + 1}</td>
            <td>
              <span className="ipd-daycare-name-line">
                <strong>{row.dialysisNumber}</strong>
                <span className={`ipd-daycare-badge ${row.visitType}`}>{row.visitType}</span>
              </span> 
            </td>
            <td>
              <Link className="patient-name-link" to={`/patients/${row.patient.id}`} title="Open patient profile"><strong>{row.patientSnapshot.name} ({row.patientSnapshot.uhid})</strong></Link>
              <small>{row.patientSnapshot.gender} · {row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</small>
              <small>{row.patientSnapshot.phone ?? '-'}</small>
            </td>
            <td>
              <small>Doct: {doctorNameOrDash(row.underDoctor?.name)}</small>
              <small>Dept: {row.underDoctor?.department ?? '-'}</small>
              <small>Patient Type: {row.insuranceType}</small>
            </td>
            <td>
              <small>Adm Dt: {formatDialysisDateTime(row.admissionDate)}</small>
              <small>Time: {formatDialysisTime(row.startTime)} - {formatDialysisTime(row.endTime)}</small>
              <small>Machine: {row.dialysisMachine.name}</small>
            </td>
            <td><strong>{row.bed.name}</strong><small>Ward: {row.bedCategory.name}</small></td>
            <td><Link className="ipd-daycare-row-action" to={`/dialysis/${row.id}`} title="View" aria-label="View admission"><IconEye /></Link></td>
          </tr>)}</tbody></table></div>
          <div className="pagination ipd-daycare-pagination"><label>Rows <select value={filters.limit} disabled={busy} onChange={(event) => { const limit = Number(event.target.value); setDraft((current) => ({ ...current, limit })); setFilters((current) => ({ ...current, limit, page: 1 })); }}><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option><option value={500}>500</option></select></label><button className="button button-secondary" disabled={busy || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button><span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span><button className="button button-secondary" disabled={busy || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button></div>
        </> : null}
      </section>
    </div>
  );
};
